/**
 * What the assistant is allowed to do, described once for every provider.
 *
 * The list is short, and the shortness is the design. Each tool maps onto
 * something the app already does when a person presses a button — navigate, open
 * a panel, type into a field — so there is no path here to a state a person could
 * not have reached themselves. Nothing writes to the family's records. The two
 * that touch data at all are `lookup`, which only reads, and `report`, which adds
 * a message addressed to the administrators and nothing else.
 *
 * `guide` is the one that matters. Everything about this assistant is meant to
 * end in a lit-up button rather than a paragraph, and `guide` is how that
 * happens, so its description works hard to be the obvious choice.
 */
import type { ToolDef } from '../providers/types';

const str = (description: string) => ({ type: 'string', description });

export const TOOLS: readonly ToolDef[] = [
  {
    name: 'guide',
    description:
      'Show them on the real screen. Lights up one button at a time, dims everything else, and waits. This is the right answer to almost every "how do I…" question — far better than writing the steps out. Either pass `howto` (an id from the HOW-TOS list, already written and translated) or your own `steps`. Keep each `say` to one short sentence: it is a caption beside a glowing button, not an explanation.',
    schema: {
      type: 'object',
      properties: {
        howto: str('Id from the HOW-TOS list. Prefer this when one fits.'),
        steps: {
          type: 'array',
          description: 'Your own walk, when no how-to fits. At most 4 steps.',
          items: {
            type: 'object',
            properties: {
              anchor: str('Anchor id from CONTROLS — the button to light up.'),
              route: str('Go to this address first, e.g. /tree.'),
              say: str('One short sentence beside the lit button.'),
            },
            required: ['say'],
          },
        },
      },
    },
  },
  {
    name: 'go',
    description: 'Take them to a screen, without a walkthrough. Use when they asked to be somewhere rather than shown something.',
    schema: {
      type: 'object',
      properties: { route: str('An address from SCREENS, e.g. /timeline.') },
      required: ['route'],
    },
  },
  {
    name: 'open',
    description: 'Open one of the panels or sheets that sit over the current screen. Their place is kept; closing it puts them back.',
    schema: {
      type: 'object',
      properties: {
        what: { type: 'string', enum: ['settings', 'notifications', 'profile', 'welcome', 'add', 'add.photo', 'add.event', 'add.milestone', 'add.recipe', 'record', 'manage', 'person', 'story'], description: 'Which one. `welcome` is the welcome message. `add.recipe` opens the recipe editor on a new recipe. `person` and `story` need an `id` from lookup.' },
        id: str('The person or story id, from lookup. Never invent one.'),
      },
      required: ['what'],
    },
  },
  {
    name: 'lookup',
    description:
      "Read the family's records. You are not given them up front, so ask here — and ask for the least you need. Returns a few short rows, never everything. Every person row starts with their id, which is what `show` and `open person` need.",
    schema: {
      type: 'object',
      properties: {
        what: { type: 'string', enum: ['people', 'relatives', 'sameAge', 'stories', 'occasions', 'places', 'me'], description: '`people` matches names. `relatives` is everybody the person asking is related to, each row labelled with how ("Your father", "Your sister") — use it for ANY question that names a relationship rather than a person, because "my father" is not a name and `people` will not find it. `occasions` is birthdays, anniversaries, yahrzeits and chagim coming up. `me` is the person asking. `sameAge` is everyone born near them — use it for "who is my age", "anyone my generation", "who is older than me".' },
        query: str('For `people`, a name. For `relatives`, the relationship word they used — father, sister, grandparents — or leave it out for everyone. For `sameAge`, a number of years instead (12 if left out).'),
      },
      required: ['what'],
    },
  },
  {
    name: 'show',
    description:
      'Put what the question is about in front of them, on the screen they are already on. Call it whenever a question is about ONE particular person, story or day — "when was Miriam born", "who is my aunt", "where is my father", "tell me about grandpa\'s story" — alongside your answer, so the screen shows what you are talking about. What it does depends on their screen: the family tree zooms to the person; the globe flies to where they live; the timeline narrows to their stories; the calendar goes to their next birthday or anniversary (or to the day); a profile screen opens their profile; anywhere else their card opens. A story opens wherever they are. Pass exactly one of person, story or day. Ids come from `lookup`, or from the snapshot when it is already on screen; never invent one. Then say only what they asked — the screen does the showing, so do not describe it or tell them what to press.',
    schema: {
      type: 'object',
      properties: {
        person: str('A person id, exactly as lookup gave it.'),
        story: str('A story id, exactly as lookup gave it.'),
        day: str('A date, YYYY-MM-DD — for a question about a day rather than a person.'),
      },
    },
  },
  {
    name: 'fill',
    description:
      'Type into a form for them. It opens with your words already in it and they press Save themselves — you never can. Use after `open` has put the right form on screen.',
    schema: {
      type: 'object',
      properties: {
        form: { type: 'string', enum: ['add.event', 'add.milestone', 'add.photo', 'report'], description: 'Which form.' },
        values: {
          type: 'object',
          description: 'Fields to fill: title, notes, caption, detail. Only what they actually told you — never invent a title.',
          properties: { title: str('Title'), notes: str('Notes'), caption: str('Caption'), detail: str('Detail') },
        },
      },
      required: ['form', 'values'],
    },
  },
  {
    name: 'report',
    description:
      'Offer to pass a message to the administrators. Use it only when somebody is reporting something broken, wishing for something the app does not do, asking a question about the family that only an administrator could answer, or asking for something to be passed on. Never for a greeting, small talk, "can you hear me?", checking whether you work, or anything you can answer yourself — just answer those. It shows them a form over the whole screen, and they decide whether to send it. Where they are and what they were doing is attached automatically — do not ask them for it.',
    schema: {
      type: 'object',
      properties: {
        kind: { type: 'string', enum: ['fault', 'idea', 'question'], description: 'fault: something broken. idea: something they wish the app did. question: something only an administrator could answer.' },
        title: str('One short line, in their own words where possible.'),
        detail: str('What happened, or what they want. A sentence or two.'),
      },
      required: ['kind', 'title'],
    },
  },
  {
    name: 'set_mode',
    description: 'Switch between Simple and Advanced. Advanced adds the globe, the timeline, the calendar, the bell and the appearance settings. Offer it when they are looking for something Simple mode hides.',
    schema: {
      type: 'object',
      properties: { mode: { type: 'string', enum: ['simple', 'advanced'] } },
      required: ['mode'],
    },
  },
  {
    name: 'set_language',
    description: 'Switch the whole app between English and Hebrew. Their place is kept.',
    schema: {
      type: 'object',
      properties: { lang: { type: 'string', enum: ['en', 'he'] } },
      required: ['lang'],
    },
  },
];

/** Tool names the runtime handles but no provider is told about — the built-in fallback's own vocabulary. */
export const INTERNAL_TOOLS = new Set(['explain', 'offerReport', 'suggest', 'sameAge']);
